/**
 * «ارسال اعلان به بیمار» — the client's half of the booking notification.
 *
 * The BFF sends (parsina.bff.clinic src/lib/appointmentNotifier.js) and answers one row
 * per channel: `{ channel, status, reason, detail, cost }`. This module owns the three
 * things the screens need around that and nothing else: the channel ORDER every surface
 * lists them in, the few WhatsApp states in which a tick would be a promise nobody can
 * keep, and turning the BFF's rows into the sentence the booking toast ends with.
 *
 * Pure on purpose — no React, no network — so each rule is testable on its own.
 */
import { CHANNELS, CHANNEL_ORDER } from './messaging';

/** The keys, in the order «ارسال پیام» renders its channel cards. */
export const NOTIFY_CHANNEL_ORDER = CHANNEL_ORDER;

/** Any list → the known keys only, in render order, once each. */
export function orderChannels(list) {
  const chosen = new Set((list || []).map((k) => String(k).toLowerCase()));
  return CHANNEL_ORDER.filter((k) => chosen.has(k));
}

/** `list` with `key` switched on or off, still in render order. */
export function withChannel(list, key, on) {
  const next = new Set(orderChannels(list));
  if (on) next.add(key);
  else next.delete(key);
  return orderChannels([...next]);
}

export const sameChannels = (a, b) => orderChannels(a).join(',') === orderChannels(b).join(',');

/**
 * The WhatsApp states in which nothing can leave the building. DISCONNECTED and PAIRING
 * are not here: the gateway recovers the first on its own and the BFF still tries, so
 * a tick there is honest.
 */
const WHATSAPP_BLOCKED = new Set(['UNLINKED', 'LOGGED_OUT']);
export const whatsappBlocked = (session) => !!session && WHATSAPP_BLOCKED.has(session.state);

/**
 * Every reason the BFF can give, so an unknown one is recognised as unknown. `opted_out`:
 * the patient's number is one this clinic stopped messaging («پیام نفرست», PM-0536 P6)
 * and no other of their numbers may take its place.
 */
export const NOTIFY_REASONS = Object.freeze([
  'no_patient', 'not_linked', 'no_phone', 'do_not_contact', 'crm_unavailable',
  'bad_phone', 'not_on_whatsapp', 'opted_out', 'whatsapp_not_linked', 'insufficient_credit',
  'rejected', 'timeout', 'unavailable',
]);

/** One row → «ارسال شد» / the reason it did not. */
export function notificationOutcome(row, t) {
  if (!row) return '';
  if (row.status === 'sent') return t('reception.notify.status.sent');
  if (row.status === 'pending') return t('reception.notify.status.pending');
  if (row.reason && NOTIFY_REASONS.includes(row.reason)) return t(`reception.notify.reasons.${row.reason}`);
  // A reason from a newer BFF still says something true: its own words, or «ارسال نشد».
  return row.detail || t('reception.notify.status.failed');
}

/** True when at least one channel did not deliver — the toast is then worth reading. */
export const notificationProblem = (rows) =>
  (rows || []).some((r) => r && (r.status === 'failed' || r.status === 'skipped'));

/**
 * The rows as one clause for the booking toast, or '' when nothing was asked for.
 * «اعلان — پیامک: ارسال شد · واتس‌اپ: واتس‌اپ کلینیک متصل نیست»
 */
export function notificationSummary(rows, t) {
  const list = (rows || []).filter(Boolean);
  if (!list.length) return '';
  const items = list
    .map((row) => t('reception.notify.item', {
      channel: CHANNELS[row.channel] ? t(CHANNELS[row.channel].labelKey) : row.channel,
      outcome: notificationOutcome(row, t),
    }))
    .join(' · ');
  return t('reception.notify.summary', { items });
}

/** The BFF's cap on the desk's own text (NOTIFY_BODY_MAX in appointmentNotifier.js). */
export const NOTIFY_BODY_MAX = 670;

/**
 * The text the BFF would send for this event, tokens unsubstituted — what the desk's
 * «ویرایش متن پیام» starts from (PM-0556). A move has its own template and never the
 * booking one, whose words say the appointment was booked. '' when the settings are
 * unreadable: then nobody here knows the text, and the editor is not offered.
 */
export function noticeBody(settings, event = 'booked') {
  if (!settings) return '';
  return event === 'rescheduled'
    ? settings.rescheduleTemplate?.body || settings.defaultRescheduleBody || ''
    : settings.template?.body || settings.defaultBody || '';
}

/**
 * What travels as `notifyBody`: the desk's text when it says something the template
 * does not, else null — so an editor opened and left alone changes nothing, and the
 * clinic's template keeps deciding.
 */
export function notifyBodyToSend(edited, template) {
  const text = String(edited ?? '').trim();
  if (!text || text === String(template ?? '').trim()) return null;
  return text;
}
